import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, CheckCircle, XCircle, RefreshCw, Sparkles } from 'lucide-react';
import { format } from 'date-fns';

interface AIDiscoveredJob {
  id: string;
  title: string;
  company: string;
  type: string;
  location: string;
  arrondissement: string | null;
  description: string;
  requirements: string[] | null;
  salary: string | null;
  deadline: string | null;
  application_url: string | null;
  contact_phone: string | null;
  source_url: string;
  ai_confidence_score: number | null;
  status: string;
  created_at: string;
}

export const AIJobDiscovery = () => {
  const [jobs, setJobs] = useState<AIDiscoveredJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [discovering, setDiscovering] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);

  const loadJobs = async () => {
    try {
      const { data, error } = await supabase
        .from('ai_discovered_jobs')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setJobs(data || []);
    } catch (error: any) {
      console.error('Error loading AI-discovered jobs:', error);
      toast.error('Erreur lors du chargement des opportunités découvertes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
  }, []);

  const startDiscovery = async () => {
    setDiscovering(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const { data, error } = await supabase.functions.invoke('discover-jobs', {
        headers: {
          Authorization: `Bearer ${session?.access_token}`
        }
      });
      
      if (error) {
        console.error('Discovery error:', error);
        throw error;
      }

      console.log('Discovery result:', data);

      if (data.errors > 0) {
        toast.success(
          `Découverte terminée: ${data.jobsAdded} nouvelles opportunités!`,
          { description: `${data.duplicatesSkipped} doublons, ${data.errors} erreurs` }
        );
      } else {
        toast.success(
          `🎉 Découverte réussie: ${data.jobsAdded} nouvelles opportunités trouvées!`,
          { description: `${data.duplicatesSkipped} doublons ignorés` }
        );
      }
      
      loadJobs();
    } catch (error: any) {
      console.error('Error discovering jobs:', error);
      toast.error('Erreur lors de la découverte', {
        description: error.message || 'Veuillez réessayer'
      });
    } finally {
      setDiscovering(false);
    }
  };

  const approveJob = async (job: AIDiscoveredJob) => {
    setProcessingId(job.id);
    try {
      // Insert into opportunities table
      const { error: insertError } = await supabase
        .from('opportunities')
        .insert([{
          title: job.title,
          company: job.company,
          type: job.type as any,
          location: job.location,
          arrondissement: job.arrondissement,
          description: job.description,
          requirements: job.requirements,
          salary: job.salary,
          deadline: job.deadline,
          application_url: job.application_url,
          contact_phone: job.contact_phone,
          status: 'approved' as any
        }]);

      if (insertError) throw insertError;

      // Update AI job status
      const { error: updateError } = await supabase
        .from('ai_discovered_jobs')
        .update({ 
          status: 'approved',
          reviewed_at: new Date().toISOString()
        })
        .eq('id', job.id);

      if (updateError) throw updateError;

      toast.success('Opportunité approuvée et publiée!');
      loadJobs();
    } catch (error: any) {
      console.error('Error approving job:', error);
      toast.error('Erreur lors de l\'approbation');
    } finally {
      setProcessingId(null);
    }
  };

  const rejectJob = async (jobId: string) => {
    setProcessingId(jobId);
    try {
      const { error } = await supabase
        .from('ai_discovered_jobs')
        .update({ 
          status: 'rejected',
          reviewed_at: new Date().toISOString()
        })
        .eq('id', jobId);

      if (error) throw error;

      toast.success('Opportunité rejetée');
      loadJobs();
    } catch (error: any) {
      console.error('Error rejecting job:', error);
      toast.error('Erreur lors du rejet');
    } finally {
      setProcessingId(null);
    }
  };

  const typeLabels: Record<string, string> = {
    emploi: 'Emploi',
    stage: 'Stage',
    formation: 'Formation',
    bourse: 'Bourse',
    volontariat: 'Volontariat'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" />
            Découverte AI d'Opportunités
          </h2>
          <p className="text-muted-foreground mt-1">
            L'IA recherche automatiquement de nouvelles opportunités sur le web
          </p>
        </div>
        <Button
          onClick={startDiscovery}
          disabled={discovering}
          className="gap-2"
        >
          {discovering ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Recherche en cours...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4" />
              Lancer la découverte
            </>
          )}
        </Button>
      </div>

      {jobs.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">
            Aucune opportunité en attente de révision. Lancez une découverte pour trouver de nouvelles opportunités!
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {jobs.map((job) => (
            <Card key={job.id} className="p-6">
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="secondary">
                        {typeLabels[job.type] || job.type}
                      </Badge>
                      {job.ai_confidence_score && (
                        <Badge variant="outline" className="text-xs">
                          Confiance: {Math.round(job.ai_confidence_score * 100)}%
                        </Badge>
                      )}
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-1">
                      {job.title}
                    </h3>
                    <p className="text-muted-foreground mb-2">
                      {job.company} • {job.location}
                      {job.arrondissement && ` • ${job.arrondissement}`}
                    </p>
                  </div>
                </div>

                <p className="text-sm text-foreground line-clamp-3">
                  {job.description}
                </p>

                {job.requirements && job.requirements.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold mb-1">Exigences:</p>
                    <ul className="text-sm text-muted-foreground list-disc list-inside">
                      {job.requirements.slice(0, 3).map((req, idx) => (
                        <li key={idx}>{req}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  {job.contact_phone && (
                    <span className="font-semibold text-primary">📞 {job.contact_phone}</span>
                  )}
                  {job.salary && (
                    <span>💰 {job.salary}</span>
                  )}
                  {job.deadline && (
                    <span>📅 Deadline: {format(new Date(job.deadline), 'dd/MM/yyyy')}</span>
                  )}
                  <span>🕐 Découvert le {format(new Date(job.created_at), 'dd/MM/yyyy HH:mm')}</span>
                </div>

                {(job.application_url || job.source_url) && (
                  <div className="space-y-1">
                    {job.application_url && (
                      <a
                        href={job.application_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline block"
                      >
                        🔗 Lien de candidature: {job.application_url}
                      </a>
                    )}
                    <a
                      href={job.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-muted-foreground hover:underline block"
                    >
                      📍 Source: {job.source_url}
                    </a>
                  </div>
                )}

                <div className="flex gap-2 pt-4 border-t">
                  <Button
                    onClick={() => approveJob(job)}
                    disabled={processingId === job.id}
                    className="flex-1 gap-2"
                    variant="default"
                  >
                    {processingId === job.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <CheckCircle className="h-4 w-4" />
                    )}
                    Approuver et Publier
                  </Button>
                  <Button
                    onClick={() => rejectJob(job.id)}
                    disabled={processingId === job.id}
                    variant="destructive"
                    className="flex-1 gap-2"
                  >
                    <XCircle className="h-4 w-4" />
                    Rejeter
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};